import type { SupabaseClient } from '@supabase/supabase-js';
import { useAccountStore } from '@/store/accountStore.ts';
import { capabilities } from '@/capabilities.ts';
import { env } from '@/config/environment';

// Resolved from the single env resolver — host-authoritative on canonical domains,
// so on share.previspro.com this is always the Prod project regardless of build.
const supabaseUrl = env.supabaseUrl;
const supabaseAnonKey = env.supabaseAnonKey;

let _client: SupabaseClient | null = null;
let _creating: Promise<SupabaseClient> | null = null;

// Embedded-mode Supabase client (uses native JWT via accessToken callback)
let _embeddedClient: SupabaseClient | null = null;
let _creatingEmbedded: Promise<SupabaseClient> | null = null;

/** Lazy singleton — Supabase module only loads when first called */
export async function getSupabase(): Promise<SupabaseClient | null> {
  if (_client) return _client;
  if (!supabaseUrl || !supabaseAnonKey) return null;

  if (!_creating) {
    _creating = import('@supabase/supabase-js').then(({ createClient }) => {
      _client = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          flowType: 'pkce',
          // We handle code exchange explicitly in App.tsx via exchangeCodeForSession(),
          // so disable auto-detection to prevent double-exchange race conditions.
          detectSessionInUrl: false,
          autoRefreshToken: true,
          persistSession: true,
        },
      });
      return _client;
    });
  }

  return _creating;
}

/** Synchronous getter — returns cached client or null if not yet initialized.
 *  Use this in useEffect callbacks where the client is guaranteed to be loaded
 *  (e.g., after ReviewLoader has already fetched a session via getSupabase). */
export function getSupabaseSync(): SupabaseClient | null {
  if (capabilities.bridge) return _embeddedClient;
  return _client ?? _embeddedClient;
}

/**
 * Lazy Supabase client for embedded mode (Unity WebView).
 * Uses the `accessToken` callback to provide the native JWT from the bridge,
 * so Realtime channels authenticate as the creator without a browser session.
 */
export async function getSupabaseForEmbedded(): Promise<SupabaseClient | null> {
  if (_embeddedClient) return _embeddedClient;
  if (!supabaseUrl || !supabaseAnonKey) return null;

  if (!_creatingEmbedded) {
    _creatingEmbedded = import('@supabase/supabase-js').then(({ createClient }) => {
      _embeddedClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
        global: {
          fetch: (url: RequestInfo | URL, init?: RequestInit) => {
            const token = useAccountStore.getState().nativeAccessToken ?? supabaseAnonKey;
            const headers = new Headers(init?.headers);
            headers.set('Authorization', `Bearer ${token}`);
            return fetch(url, { ...init, headers });
          },
        },
        realtime: {
          params: {
            apikey: supabaseAnonKey,
          },
          accessToken: async () => {
            return useAccountStore.getState().nativeAccessToken ?? supabaseAnonKey;
          },
        },
      });
      return _embeddedClient;
    });
  }

  return _creatingEmbedded;
}

/** Returns the embedded Supabase client synchronously (for Realtime auth refresh). */
export function getEmbeddedSupabaseSync(): SupabaseClient | null {
  return _embeddedClient;
}

/** Get the appropriate Supabase client for the current mode (embedded vs standard). */
export async function getSupabaseForMode(): Promise<SupabaseClient | null> {
  if (capabilities.bridge && !useAccountStore.getState().standaloneAuthMode) {
    return getSupabaseForEmbedded();
  }
  return getSupabase();
}

export function isSupabaseConfigured(): boolean {
  return !!(supabaseUrl && supabaseAnonKey);
}

// 2026-09-21 (frozen-CTA fix) - every edge-function call must eventually settle: `forceFlush()`
// (trackEvent.ts) awaits this, and launch-path handlers await forceFlush() before they can call
// sendToNative, so an unbounded fetch here froze the button with no spinner if the server never
// answered. AbortSignal.timeout() is the spec-clean way to bound a fetch; this runs in WKWebView
// (iOS 17+, has it) and jsdom (vitest, also has it), but a manual AbortController fallback covers
// any host without it. Callers can override via `options.timeoutMs`.
const EDGE_FN_TIMEOUT_MS = 15000;

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Call a Supabase Edge Function using raw fetch.
 * Bypasses the Supabase JS `functions.invoke` which can throw AbortError
 * due to internal AbortController issues in v2.95+.
 * Retries once on transient errors by default (AbortError, network failures).
 * In embedded mode, automatically retries on 401/403 by refreshing the token via the bridge.
 * Each attempt is bounded by `options.timeoutMs` (default EDGE_FN_TIMEOUT_MS) — a timeout on one
 * attempt is treated as transient, same as a dropped connection, and can still trigger the retry.
 */
async function invokeEdgeFnCore(
  functionName: string,
  body: Record<string, unknown>,
  options?: { retry?: boolean; onResponse?: (data: any) => void; timeoutMs?: number },
): Promise<any> {
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Supabase not configured');

  const retry = options?.retry !== false;
  const maxAttempts = retry ? 2 : 1;
  const timeoutMs = options?.timeoutMs ?? EDGE_FN_TIMEOUT_MS;

  // Get JWT for authenticated calls
  let jwt = supabaseAnonKey;
  if (capabilities.bridge) {
    // Embedded mode: native token from Unity takes priority
    const nativeToken = useAccountStore.getState().nativeAccessToken;
    if (nativeToken) {
      jwt = nativeToken;
    } else if (useAccountStore.getState().standaloneAuthMode) {
      // Editor standalone auth — use browser Supabase session
      const sb = await getSupabase();
      if (sb) {
        try {
          const { data } = await sb.auth.getSession();
          if (data.session?.access_token) jwt = data.session.access_token;
        } catch { /* fall back to anon key */ }
      }
    }
  } else {
    // Standalone: try browser session
    const sb = await getSupabase();
    if (sb) {
      try {
        const { data } = await sb.auth.getSession();
        if (data.session?.access_token) jwt = data.session.access_token;
      } catch {
        // stale session — fall back to anon key
      }
    }
  }

  const url = `${supabaseUrl}/functions/v1/${functionName}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${jwt}`,
    'apikey': supabaseAnonKey,
  };

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: timeoutSignal(timeoutMs),
      });
      const data = await resp.json();
      options?.onResponse?.(data);
      if (!resp.ok) {
        const error = new Error(data?.error || `Edge function returned ${resp.status}`);
        (error as any).status = resp.status;
        throw error;
      }
      return data;
    } catch (err) {
      // AbortSignal.timeout()'s abort reason is a "TimeoutError" DOMException, not "AbortError"
      // (2026-09-21) — a bounded fetch needs both names classified as transient/retryable, the
      // same as any other dropped connection, so existing callers' error handling (and the retry
      // above) keeps working whether the fetch was aborted by us or failed on its own. Checked by
      // `.name` rather than `instanceof DOMException`: Node's built-in AbortSignal.timeout()
      // constructs its reason from a different realm than jsdom's global DOMException in tests
      // (same symptom could show up in any cross-realm host), so `instanceof` silently misses it.
      const errName = (err as { name?: unknown } | null)?.name;
      const isTransient =
        errName === 'AbortError' || errName === 'TimeoutError' ||
        (err instanceof TypeError && /fetch|network|load failed/i.test((err as Error).message));
      if (isTransient && attempt < maxAttempts - 1) {
        console.warn(`Edge function ${functionName} call failed, retrying...`, (err as Error).message);
        await new Promise(r => setTimeout(r, 500));
        continue;
      }
      throw err;
    }
  }
}

export async function invokeEdgeFn(
  functionName: string,
  body: Record<string, unknown>,
  options?: { retry?: boolean; onResponse?: (data: any) => void; timeoutMs?: number },
): Promise<any> {
  try {
    return await invokeEdgeFnCore(functionName, body, options);
  } catch (err) {
    const status = (err as any).status;
    if (capabilities.bridge && (status === 401 || status === 403)) {
      const nativeToken = useAccountStore.getState().nativeAccessToken;
      if (nativeToken) {
        // Normal embedded — refresh via Unity bridge
        const { requestTokenRefresh } = await import('@/bridge/embeddedBridge.ts');
        if (await requestTokenRefresh()) {
          return await invokeEdgeFnCore(functionName, body, options);
        }
      } else if (useAccountStore.getState().standaloneAuthMode) {
        // Editor standalone auth — refresh browser session
        const sb = await getSupabase();
        if (sb) {
          const { error } = await sb.auth.refreshSession();
          if (!error) return await invokeEdgeFnCore(functionName, body, options);
        }
      }
      throw new Error('Session expired — please restart the app to sign in again');
    }
    throw err;
  }
}

/**
 * Require an existing Supabase Auth session (OAuth or email).
 * Throws if the user is not signed in — the app-level auth gate in App.tsx
 * ensures users are signed in before reaching any feature that calls this.
 */
export async function requireAuth(): Promise<void> {
  // Native token from bridge (embedded mode) counts as authenticated
  if (useAccountStore.getState().nativeAccessToken) return;

  // Editor standalone auth — fall through to check browser session
  if (capabilities.bridge && !useAccountStore.getState().standaloneAuthMode) {
    throw new Error('Sign in via Settings to use AI features');
  }

  const sb = await getSupabase();
  if (!sb) throw new Error('Sign in required');

  try {
    const { data } = await sb.auth.getSession();
    if (data.session) return;
  } catch {
    // stale session
  }

  throw new Error('Sign in required');
}
